/**
 * NOS-140: Peer fundraiser share page /campaign/:slug/fundraise/:token
 */
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { PublicSitePage } from "@/components/PublicSitePage"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { extractDescriptionFromPageSections } from "@/lib/seo/extractMetaDescription"
import { usePublicSiteShell } from "@/hooks/usePublicSiteShell"
import { BlockRenderer } from "@/components/website-builder/BlockRenderer"
import { CampaignProgressBar } from "@/components/campaigns/CampaignProgressBar"
import type { Organization, OrgCustomizations, PageSection } from "@/templates/types"
import { Button } from "@/components/ui/button"
import { blockJsonToPageSections } from "@/lib/blockContentToPageSections"
import { campaignEnded, daysRemaining } from "@/lib/campaigns/campaignDates"
import type { Tables } from "@/types/database"
import { toast } from "sonner"
import { Copy, Check, Share2 } from "lucide-react"

type CampaignRow = Tables<"campaigns">
type FundraiserRow = Tables<"campaign_fundraisers">

export interface FundraiserSharePageProps {
  orgSlug: string
  campaignSlug: string
  shareToken: string
  org?: Organization | null
  customizations?: OrgCustomizations | null
  basePath?: string
}

async function fetchCampaignTotals(campaignId: string): Promise<{ raised_cents: number; donor_count: number }> {
  const { data, error } = await supabase.rpc("get_campaign_public_totals", { cid: campaignId })
  if (error || !data?.length) return { raised_cents: 0, donor_count: 0 }
  const row = data[0] as { raised_cents: number; donor_count: number }
  return { raised_cents: Number(row.raised_cents), donor_count: Number(row.donor_count) }
}

async function fetchFundraiserTotals(fundraiserId: string): Promise<{ raised_cents: number; donor_count: number }> {
  const { data, error } = await supabase.rpc("get_fundraiser_public_totals", { fid: fundraiserId })
  if (error || !data?.length) return { raised_cents: 0, donor_count: 0 }
  const row = data[0] as { raised_cents: number; donor_count: number }
  return { raised_cents: Number(row.raised_cents), donor_count: Number(row.donor_count) }
}

export function FundraiserSharePage({
  orgSlug,
  campaignSlug,
  shareToken,
  org: orgProp,
  customizations: custProp,
  basePath = "",
}: FundraiserSharePageProps) {
  const shell = usePublicSiteShell(orgSlug, {
    org: orgProp,
    customizations: custProp,
    basePath,
  })
  const { org: resolvedOrg, cust, orgName, primaryColor } = shell

  const [campaign, setCampaign] = useState<CampaignRow | null>(null)
  const [fundraiser, setFundraiser] = useState<FundraiserRow | null>(null)
  const [campaignRaised, setCampaignRaised] = useState(0)
  const [personalRaised, setPersonalRaised] = useState(0)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [copied, setCopied] = useState(false)

  const orgId = resolvedOrg?.id
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!campaignSlug || !shareToken) {
        setNotFound(true)
        setLoading(false)
        return
      }
      if (!orgId) return

      const { data: fr, error: frErr } = await supabase
        .from("campaign_fundraisers")
        .select(
          `id, name, share_token, personal_goal_cents, campaign_id, org_id,
          campaigns!inner (
            id, slug, title, org_id, goal_cents, end_date, cover_image_url, content_sections, is_published
          )`
        )
        .eq("share_token", shareToken)
        .maybeSingle()

      if (cancelled) return
      if (frErr || !fr) {
        setNotFound(true)
        setLoading(false)
        return
      }

      const row = fr as FundraiserRow & { campaigns: CampaignRow }
      const camp = row.campaigns
      if (!camp.is_published || camp.slug !== campaignSlug) {
        setNotFound(true)
        setLoading(false)
        return
      }

      const { campaigns: _c, ...frFields } = row
      setFundraiser(frFields as FundraiserRow)
      setCampaign(camp)

      const [campTotals, fundTotals] = await Promise.all([
        fetchCampaignTotals(camp.id),
        fetchFundraiserTotals(row.id),
      ])

      if (!cancelled) {
        setCampaignRaised(campTotals.raised_cents)
        setPersonalRaised(fundTotals.raised_cents)
        setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [orgId, campaignSlug, shareToken])

  const sectionBlocks: PageSection[] = useMemo(() => {
    if (!campaign) return []
    return blockJsonToPageSections(campaign.content_sections, campaign.org_id)
  }, [campaign])

  const ended = campaign ? campaignEnded(campaign.end_date) : false
  const daysLeft = campaign ? daysRemaining(campaign.end_date) : null

  const metaDescription = useMemo(() => {
    if (!campaign || !fundraiser) return ""
    const base = extractDescriptionFromPageSections(sectionBlocks)
    const intro = `${fundraiser.name} is fundraising for ${campaign.title}.`
    const rest = base ? ` ${base}` : ""
    const s = `${intro}${rest}`
    return s.length <= 160 ? s : `${s.slice(0, 159)}…`
  }, [campaign, fundraiser, sectionBlocks])

  const canonicalUrl = useMemo(() => {
    if (!resolvedOrg || !campaign) return ""
    const origin = getPublicSiteOrigin(resolvedOrg)
    return `${origin}/campaign/${campaign.slug}/fundraise/${shareToken}`
  }, [resolvedOrg, campaign, shareToken])

  const donateSearch =
    campaign && fundraiser && !ended
      ? `?campaign_id=${campaign.id}&fundraiser_id=${fundraiser.id}`
      : ""
  const donateHref = `${basePath}/donate${donateSearch}`

  const pageUrl = useMemo(
    () =>
      canonicalUrl ||
      (typeof window !== "undefined" ? window.location.href : ""),
    [canonicalUrl]
  )

  async function handleNativeShare() {
    const url = pageUrl
    const title = fundraiser && campaign ? `${fundraiser.name} — ${campaign.title}` : "Fundraiser"
    if (navigator.share) {
      try {
        await navigator.share({ title, url })
      } catch {
        // user cancelled
      }
    } else {
      await navigator.clipboard.writeText(url)
      toast.success("Link copied")
    }
  }

  function copyLink() {
    void navigator.clipboard.writeText(pageUrl)
    setCopied(true)
    toast.success("Link copied")
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading || shell.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (notFound || shell.notFound || !campaign || !fundraiser || !resolvedOrg) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-4">
        <h1 className="text-2xl font-bold">404</h1>
        <p className="text-muted-foreground text-center">This fundraiser page wasn’t found.</p>
        <Button asChild variant="outline">
          <Link to={`${basePath}/campaign`}>View campaigns</Link>
        </Button>
      </div>
    )
  }

  const personalGoalForBar =
    fundraiser.personal_goal_cents && fundraiser.personal_goal_cents > 0
      ? fundraiser.personal_goal_cents
      : campaign.goal_cents > 0
        ? campaign.goal_cents
        : Math.max(personalRaised, 1)

  return (
    <PublicSitePage
      shell={shell}
      className="bg-background"
      head={
        canonicalUrl ? (
          <SEOHead
            title={`${fundraiser.name} — ${campaign.title} | ${orgName}`}
            description={metaDescription}
            canonicalUrl={canonicalUrl}
            ogType="website"
            imageUrl={campaign.cover_image_url}
          />
        ) : null
      }
    >
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        {ended ? (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            This campaign has ended.
          </div>
        ) : null}

        {campaign.cover_image_url ? (
          <div className="mb-8 aspect-video overflow-hidden rounded-lg">
            <img src={campaign.cover_image_url} alt="" className="h-full w-full object-cover" />
          </div>
        ) : null}

        <header className="mb-6 space-y-3">
          <p className="text-sm font-medium text-muted-foreground">{fundraiser.name} is fundraising for</p>
          <h1 className="text-3xl font-bold">{campaign.title}</h1>
          {!ended && daysLeft != null && daysLeft >= 0 ? (
            <p className="text-sm text-muted-foreground">{daysLeft} days left in campaign</p>
          ) : null}
        </header>

        <section className="mb-6 space-y-4 rounded-lg border bg-card p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {fundraiser.name}&apos;s progress
          </h2>
          <CampaignProgressBar
            raisedCents={personalRaised}
            goalCents={personalGoalForBar}
            ended={ended}
          />
          <div className="text-sm text-muted-foreground">
            <p>
              Campaign total:{" "}
              <span className="font-medium text-foreground">
                ${(campaignRaised / 100).toLocaleString()}
              </span>{" "}
              raised
            </p>
          </div>
        </section>

        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Button size="lg" asChild style={{ backgroundColor: primaryColor, borderColor: primaryColor }}>
            <Link to={ended ? `${basePath}/donate` : donateHref}>
              {ended ? `Support ${orgName}` : "Donate to this fundraiser"}
            </Link>
          </Button>
          <div className="flex gap-2">
            <Button size="lg" variant="outline" type="button" onClick={() => void handleNativeShare()}>
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </Button>
            <Button size="lg" variant="outline" type="button" onClick={() => copyLink()}>
              {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
              Copy link
            </Button>
          </div>
        </div>

        {/* NOS-221: no `prose` wrapper around section blocks (see BlogPostPage) */}
        <div className="max-w-none">
          <p className="text-sm text-muted-foreground mb-4">
            <Link to={`${basePath}/campaign/${campaign.slug}`} className="underline">
              About this campaign →
            </Link>
          </p>
          {sectionBlocks
            .sort((a, b) => a.order_index - b.order_index)
            .map((section) => (
              <BlockRenderer key={section.id} section={section} customizations={cust} org={resolvedOrg} />
            ))}
        </div>
      </main>
    </PublicSitePage>
  )
}
